import { useRef } from 'react';

import { useAuth } from '../../hooks/useAuth';
import { isInstalledApp } from '../../utils/displayMode';
import InstalledSignIn from './InstalledSignIn';
import LandingNav from './landing/LandingNav';
import LandingHero from './landing/LandingHero';
import FilmBand from './landing/FilmBand';
import PracticesBand from './landing/PracticesBand';
import SearchesBand from './landing/SearchesBand';
import StatsLedger from './landing/StatsLedger';
import InsideSection from './landing/InsideSection';
import PricingBand from './landing/PricingBand';
import FaqSection from './landing/FaqSection';
import ClosingBand from './landing/ClosingBand';
import TrustStrip from './landing/TrustStrip';
import LandingFooter from './landing/LandingFooter';
import TrustCenterSlide from './landing/TrustCenterSlide';

/**
 * Logged-out landing page — the chambers-ledger page, with the parts Ajay
 * picked from the 2026-11-01 refresh build: the sticky header, the practice-tab
 * hero, "Three practices. One chamber." right under the film, the FAQ, the
 * closing call to action and the footer. The numbered sections are listed, in
 * this order, in landing/homepageContent.ts (PAGE_SECTIONS). Thin composer: auth
 * state comes from useAuth(); every section under ./landing is purely
 * presentational and receives only {onLogin, loading, error?}.
 *
 * The Trust Center and the policies are not on the page itself: a slim Trust
 * strip sits above the footer, and every link to them opens TrustCenterSlide, a
 * full-page view that slides in over the page (Ajay, 2026-11-03). It renders
 * outside the page wrapper so it can make the page inert while it is open.
 *
 * Fixed palette by design — never dark-mode toggled. Dark chambers, with the
 * practices, searches, pricing and FAQ on parchment.
 *
 * Launched from the home screen (installed PWA), the visitor already chose
 * iAdvocate — they get the one-screen InstalledSignIn instead of the
 * marketing page. Browser tabs keep the full landing.
 */
const LoginForm = () => {
  const { login, loading, error } = useAuth();
  const pageRef = useRef<HTMLDivElement>(null);

  if (isInstalledApp()) {
    return <InstalledSignIn onLogin={login} loading={loading} error={error} />;
  }

  return (
    <>
      <div id="top" ref={pageRef} className="min-h-screen bg-chamber-rail font-instrument">
        <LandingNav onLogin={login} loading={loading} />
        <main>
          <LandingHero onLogin={login} loading={loading} error={error} />
          <FilmBand onLogin={login} loading={loading} />
          <PracticesBand onLogin={login} loading={loading} />
          <SearchesBand />
          <StatsLedger />
          <InsideSection />
          <PricingBand onLogin={login} loading={loading} />
          <FaqSection />
          <ClosingBand onLogin={login} loading={loading} />
          <TrustStrip />
        </main>
        <LandingFooter />
      </div>
      <TrustCenterSlide pageRef={pageRef} />
    </>
  );
};

export default LoginForm;
